import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { Subscription, merge } from 'rxjs';
import { APP_ROUTES } from '../../../core/constants/app-routes';
import { FLASH_MESSAGES } from '../../../core/constants/flash-messages';
import { Logement } from '../../../core/models/logement.model';
import { FlashService } from '../../../core/services/flash.service';
import { LocataireService } from '../../../core/services/locataire.service';
import { LogementService } from '../../../core/services/logement.service';
import {
  applyDureeBailDefaut,
  createLocataireForm,
  dureeBailFormErrorKey,
  dureeBailFormHintKey,
  filterOpenLogements,
  isDureeBailFormInvalid,
  locataireFromFormValue,
} from '../locataire-form';
import { LocataireFormFieldsComponent } from '../locataire-form-fields/locataire-form-fields.component';

@Component({
  selector: 'app-ajout-locataire',
  templateUrl: './ajout-locataire.component.html',
  styleUrls: ['./ajout-locataire.component.css'],
  imports: [
    ReactiveFormsModule,
    RouterLink,
    FaIconComponent,
    TranslateModule,
    LocataireFormFieldsComponent,
  ],
})
export class AjoutLocataireComponent implements OnInit, OnDestroy {
  logements: Logement[] = [];
  loadingLogements = true;
  logementsError = false;
  saving = false;
  error = false;
  selectedMeuble = false;
  readonly routes = APP_ROUTES;

  readonly form = createLocataireForm(this.fb, () => this.selectedMeuble);

  private formSubs?: Subscription;

  constructor(
    private readonly fb: FormBuilder,
    private readonly locataireService: LocataireService,
    private readonly logementService: LogementService,
    private readonly flashService: FlashService,
    private readonly translate: TranslateService,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.logementService.getLogement().subscribe({
      next: (logements) => {
        this.logements = filterOpenLogements(logements ?? [], (key) =>
          this.translate.instant(key)
        );
        this.loadingLogements = false;
      },
      error: () => {
        this.logementsError = true;
        this.loadingLogements = false;
      },
    });

    this.formSubs = merge(
      this.form.controls.logementId.valueChanges,
      this.form.controls.etudiant.valueChanges
    ).subscribe(() => {
      this.selectedMeuble = applyDureeBailDefaut(this.form, this.logements);
    });
  }

  ngOnDestroy(): void {
    this.formSubs?.unsubscribe();
  }

  get dureeBailHintKey(): string {
    return dureeBailFormHintKey(this.form, this.selectedMeuble);
  }

  get dureeBailErrorKey(): string {
    return dureeBailFormErrorKey(this.form, this.selectedMeuble);
  }

  get dureeBailInvalid(): boolean {
    return isDureeBailFormInvalid(this.form);
  }

  submit(): void {
    if (this.form.invalid || this.saving) {
      this.form.markAllAsTouched();
      return;
    }

    const locataire = locataireFromFormValue(this.form.getRawValue());

    this.saving = true;
    this.error = false;

    this.locataireService.create(locataire).subscribe({
      next: (created) => {
        if (created?.densiteAlerteDto) {
          this.flashService.success(FLASH_MESSAGES.locataireDensiteAlerte, 5000);
        }
        const id = created?.idLocataireDto;
        if (id) {
          void this.router.navigate([APP_ROUTES.locataires, id]);
          return;
        }
        void this.router.navigate([APP_ROUTES.locataires]);
      },
      error: () => {
        this.saving = false;
        this.error = true;
      },
    });
  }
}
